/**
 * F10 — Admin venue submissions review queue.
 *
 * Route: /venues/submissions
 * Permission: venues:moderate
 *
 * FIFO queue of user-submitted new venue requests. Filters synced to URL.
 * Clicking a row navigates to /venues/submissions/:id.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Chip,
  EmptyState,
  FormField,
  Select,
  Skeleton,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { VENUE_SUBMISSION_STATUSES } from '@bravemove/types';
import type { AdminPageMeta, VenueSubmissionStatus } from '@bravemove/types';
import {
  adminVenueApi,
  type VenueSubmissionListOpts,
  type VenueSubmissionForAdmin,
} from '../api/venue.js';
import { translateApiError } from '../auth/errors.js';
import { PageHeader } from '../components/PageHeader';
import { AdminPagination } from '../components/AdminPagination';

const PAGE_LIMIT = 25;

const STATUS_TONES: Record<
  VenueSubmissionStatus,
  'success' | 'warning' | 'neutral' | 'danger' | 'info'
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  merged: 'info',
};

function buildListOpts(raw: {
  status: string | null;
  countryCode: string | null;
  potentialDuplicate: boolean;
  page: number;
}): VenueSubmissionListOpts {
  const opts: Record<string, string | number | boolean> = { perPage: PAGE_LIMIT, page: raw.page };
  // 'all' is the explicit "no status filter" sentinel — send no `status` param so
  // the backend returns every status. (An empty/absent param would default the
  // dropdown back to 'pending', so 'all' must be carried explicitly.)
  if (raw.status && raw.status !== 'all') opts.status = raw.status;
  if (raw.countryCode) opts.countryCode = raw.countryCode;
  if (raw.potentialDuplicate) opts.potentialDuplicate = true;
  return opts as unknown as VenueSubmissionListOpts;
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

export function AdminVenueSubmissionsQueuePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 'all' is the explicit show-everything value; default 'pending' on first visit.
  const statusParam = searchParams.get('status') ?? 'pending';
  const countryParam = searchParams.get('countryCode') ?? 'LB';
  const dupParam = searchParams.get('potentialDuplicate') === '1';
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly VenueSubmissionForAdmin[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: VenueSubmissionListOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminVenueApi.listSubmissions(opts);
        setRows(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(
      buildListOpts({
        status: statusParam,
        countryCode: countryParam || null,
        potentialDuplicate: dupParam,
        page: pageParam,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam, countryParam, dupParam, pageParam]);

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    });
  }

  function goToPage(page: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  }

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('admin:venue.submissionStatus.all') },
      ...VENUE_SUBMISSION_STATUSES.map((s) => ({
        value: s,
        label: t(`admin:venue.submissionStatus.${s}`),
      })),
    ],
    [t],
  );

  const countryOptions = useMemo(() => [{ value: 'LB', label: 'Lebanon (LB)' }], []);

  const columns = useMemo<readonly TableColumn<VenueSubmissionForAdmin>[]>(
    () => [
      {
        key: 'submitted',
        header: t('admin:venue.table.submittedAt'),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.createdAt, locale)}</span>
        ),
      },
      {
        key: 'name',
        header: t('admin:venue.table.name'),
        cell: (row) => <span className="font-medium text-[13px] text-ink">{row.payload.name}</span>,
      },
      {
        key: 'location',
        header: t('admin:venue.table.location'),
        cell: (row) => <span className="text-[12.5px] text-ink-2">{row.payload.cazaCode}</span>,
      },
      {
        key: 'status',
        header: t('admin:venue.table.status'),
        cell: (row) => (
          <Badge tone={STATUS_TONES[row.status]} size="sm">
            {t(`admin:venue.submissionStatus.${row.status}`)}
          </Badge>
        ),
      },
      {
        key: 'duplicate',
        header: t('admin:venue.table.duplicate'),
        cell: (row) =>
          row.potentialDuplicateOf ? (
            <Chip selected>{t('admin:venue.filters.potentialDuplicate')}</Chip>
          ) : (
            <span className="text-[12px] text-ink-3">—</span>
          ),
      },
      {
        key: 'submitter',
        header: t('admin:venue.table.submitter'),
        cell: (row) => (
          <span className="text-[12px] text-ink-3">
            {row.submittedByUserId ? (row.submitterDisplayName ?? row.submittedByUserId) : 'Anon'}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:venue.pageTitle.submissions')} />

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FormField label={t('admin:venue.filters.status')}>
          <Select
            value={statusParam}
            onChange={(e) => setParam('status', e.target.value)}
            options={statusOptions}
          />
        </FormField>
        <FormField label={t('admin:venue.filters.country')}>
          <Select
            value={countryParam}
            onChange={(e) => setParam('countryCode', e.target.value)}
            options={countryOptions}
          />
        </FormField>
        <FormField label={t('admin:venue.filters.potentialDuplicate')}>
          <label className="flex items-center gap-2 mt-1 cursor-pointer">
            <input
              type="checkbox"
              checked={dupParam}
              onChange={(e) => setParam('potentialDuplicate', e.target.checked ? '1' : '')}
              className="rounded border-outline"
            />
            <span className="text-[13px] text-ink">
              {t('admin:venue.filters.potentialDuplicate')}
            </span>
          </label>
        </FormField>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="receipt" size={36} color="currentColor" />}
          title={t('admin:venue.table.emptySubmissions')}
        />
      ) : (
        <Table
          data={rows as VenueSubmissionForAdmin[]}
          columns={columns as TableColumn<VenueSubmissionForAdmin>[]}
          rowKey={(row) => row.id}
          onRowPress={(row) => navigate(`/venues/submissions/${row.id}`)}
          empty={t('admin:venue.table.emptySubmissions')}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="venue-submissions-pagination"
        />
      ) : null}
    </div>
  );
}
